import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type ToastType = "success" | "error" | "info";

type ToastInput = {
  type?: ToastType;
  title: string;
  description?: string;
};

type ToastItem = ToastInput & {
  id: string;
  type: ToastType;
};

type ToastContextValue = {
  notify: (toast: ToastInput) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const toastStyle: Record<ToastType, { border: string; icon: typeof CheckCircle2; iconClass: string }> = {
  success: { border: "border-emerald-200", icon: CheckCircle2, iconClass: "text-emerald-600" },
  error: { border: "border-rose-200", icon: AlertCircle, iconClass: "text-rose-600" },
  info: { border: "border-blue-200", icon: Info, iconClass: "text-brand" }
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (toast: ToastInput) => {
      const id = crypto.randomUUID();
      const next: ToastItem = { ...toast, id, type: toast.type ?? "info" };
      setToasts((current) => [next, ...current].slice(0, 4));
      window.setTimeout(() => dismiss(id), toast.type === "error" ? 5200 : 3600);
    },
    [dismiss]
  );

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-20 z-[70] w-[calc(100vw-2rem)] max-w-sm space-y-2" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => {
          const style = toastStyle[toast.type];
          const Icon = style.icon;
          return (
            <div key={toast.id} className={`rounded-lg border ${style.border} bg-white p-3 shadow-panel`}>
              <div className="flex items-start gap-3">
                <Icon size={18} className={`mt-0.5 shrink-0 ${style.iconClass}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-ink">{toast.title}</p>
                  {toast.description ? <p className="mt-1 text-xs font-medium leading-5 text-slate-600">{toast.description}</p> : null}
                </div>
                <button
                  className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                  type="button"
                  aria-label="Đóng thông báo"
                  onClick={() => dismiss(toast.id)}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside ToastProvider");
  }
  return context;
}
